/**
 * EmergencyGarageOfferModal – Notfallgarage-Angebot bei Kapazitätsfehlern (NT-VIERTEL-01 §6,
 * NT-HOTFIX-VIERTEL-01).
 *
 * Der EINZIGE Einstiegspunkt, um eine Notfallgarage neu zu mieten: wird von jedem Frontend-Flow
 * aufgerufen, dessen zugehöriger Backend-Endpunkt bei vollem Ziellager einen `INSUFFICIENT_SLOTS`-
 * Fehler mit `details.emergencyGarageAvailable=true` liefert (rethrowWithEmergencyGarageHint(),
 * emergency-garage.service.ts — aktuell: Markt-Kauf, Lagerhaus-Versenden/Fahrzeug→Lager-Transfer,
 * Ernte, Labor-Sammeln [beide Systeme]). Der Dauer-Button im Tab VIERTEL wurde bewusst entfernt
 * (siehe viertel-tab.ts) — dieses Modal ist der alleinige Ersatz-Einstieg.
 *
 * Nutzung an jeder betroffenen Call-Site:
 *   } catch (err) {
 *     if (tryOfferEmergencyGarage(err, () => { void reload(); })) return;
 *     showToast(err instanceof ApiError ? err.message : 'Fehler.', 'error');
 *   }
 */

import { api, ApiError } from '../services/api.js';
import { showToast } from '../utils/toast.js';

const MODAL_ID = 'emergency-garage-offer-modal';

interface EmergencyGarageHint {
  minFeeCents:     string;
  includedHours:   number;
  hourlyBaseCents: string;
  escalation:      number;
  slots:           number;
}

function esc(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function fmtEur(cents: string): string {
  return `${(Number(cents) / 100).toFixed(2)} ₦`;
}

/** Liest den Hint defensiv aus `err.details` — fehlende/falsch typisierte Felder fallen auf
 *  plausible Defaults zurück (deckt sich mit den EMERGENCY_GARAGE_*-Konstanten im Backend). */
function readHint(details: Record<string, unknown>): EmergencyGarageHint {
  return {
    minFeeCents:     typeof details.minFeeCents === 'string' ? details.minFeeCents : '5000',
    includedHours:   typeof details.includedHours === 'number' ? details.includedHours : 6,
    hourlyBaseCents: typeof details.hourlyBaseCents === 'string' ? details.hourlyBaseCents : '500',
    escalation:      typeof details.escalation === 'number' ? details.escalation : 1.15,
    slots:           typeof details.slots === 'number' ? details.slots : 8,
  };
}

/**
 * Prüft, ob `err` ein `INSUFFICIENT_SLOTS`-Fehler mit Notfallgarage-Angebot ist — zeigt in diesem
 * Fall das Angebots-Modal statt eines generischen Fehlertoasts.
 *
 * @param onRented Wird nach erfolgreicher Anmietung aufgerufen (z.B. um die aufrufende Seite neu
 *   zu laden). Die ursprüngliche Aktion wird NICHT automatisch wiederholt — der Spieler versucht
 *   sie nach der Anmietung selbst erneut.
 * @returns true = Modal gezeigt, Aufrufer soll KEINEN eigenen Fehlertoast mehr zeigen.
 *          false = nicht zutreffend (falscher Fehlercode, kein Angebot, oder bereits eine
 *          Notfallgarage aktiv), Aufrufer nimmt den normalen Fehlerpfad.
 */
export function tryOfferEmergencyGarage(err: unknown, onRented?: () => void): boolean {
  if (!(err instanceof ApiError) || err.code !== 'INSUFFICIENT_SLOTS') return false;
  if (err.details.emergencyGarageAvailable !== true) return false;

  openModal(err.message, readHint(err.details), onRented);
  return true;
}

function openModal(reasonMessage: string, hint: EmergencyGarageHint, onRented?: () => void): void {
  removeModal();
  injectStyles();

  const escalationPct = String(Math.round((hint.escalation - 1) * 100));

  const overlay = document.createElement('div');
  overlay.id = MODAL_ID;
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `
    <div class="modal ego-modal" role="dialog" aria-modal="true" aria-label="Notfallgarage anmieten">
      <div class="modal-header">
        <h2>KEIN LAGERPLATZ MEHR</h2>
      </div>
      <div class="modal-body">
        <p class="ego-reason">${esc(reasonMessage)}</p>
        <p class="ego-offer">Notfallgarage anmieten? Sofort fällig: <strong>${fmtEur(hint.minFeeCents)}</strong>
          (deckt die ersten ${String(hint.includedHours)} Std.).</p>
        <ul class="ego-list">
          <li>Danach ${fmtEur(hint.hourlyBaseCents)}/h, steigend (+${escalationPct}&nbsp;% pro vollem Tag)</li>
          <li>${String(hint.slots)} Slots, kein Grow/Labor/Fahrzeugplatz, max. 1 gleichzeitig</li>
          <li>Reicht dein Guthaben nicht, sammelt sich ein Rückstand — nie ins Minus</li>
        </ul>
        <label class="ego-confirm-label">
          <input type="checkbox" id="ego-confirm-checkbox"> Ich habe die Kosten verstanden
        </label>
      </div>
      <div class="modal-actions">
        <button class="ego-btn" id="ego-cancel" type="button">ABBRECHEN</button>
        <button class="ego-btn ego-btn--rent" id="ego-confirm-rent" type="button" disabled>NOTFALLGARAGE MIETEN</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) removeModal();
  });
  document.getElementById('ego-cancel')?.addEventListener('click', removeModal);
  document.getElementById('ego-confirm-checkbox')?.addEventListener('change', (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    const btn = document.getElementById('ego-confirm-rent') as HTMLButtonElement | null;
    if (btn) btn.disabled = !checked;
  });
  document.getElementById('ego-confirm-rent')?.addEventListener('click', () => {
    void handleConfirmRent(onRented);
  });
}

async function handleConfirmRent(onRented?: () => void): Promise<void> {
  const btn = document.getElementById('ego-confirm-rent') as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    await api.post<{ id: string }>('/viertel/emergency-garage/rent', { confirmCosts: true });
    showToast('Notfallgarage gemietet.', 'info');
    removeModal();
    onRented?.();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Anmietung fehlgeschlagen.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'NOTFALLGARAGE MIETEN'; }
  }
}

function removeModal(): void {
  document.getElementById(MODAL_ID)?.remove();
}

function injectStyles(): void {
  const styleId = 'emergency-garage-offer-modal-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Shell = .modal-backdrop/.modal aus dashboard.css. .ego-modal ist nur die
       Orange-Akzent-Themenvariante (analog Notfallgarage-Status im Tab VIERTEL). */
    .ego-modal { border-color: var(--accent-orange); }
    .ego-modal .modal-header h2 { color: var(--accent-orange); }
    .ego-reason {
      color: var(--text-muted);
      font-size: var(--font-size-sm);
      margin-bottom: var(--space-3);
    }
    .ego-offer {
      color: var(--text-primary);
      font-size: var(--font-size-sm);
      margin-bottom: var(--space-2);
    }
    .ego-list {
      color: var(--text-muted);
      font-size: var(--font-size-xs);
      line-height: 1.7;
      margin: 0 0 var(--space-3);
      padding-left: 18px;
    }
    .ego-confirm-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: var(--font-size-xs);
      color: var(--text-primary);
    }
    .ego-btn {
      font-family: var(--font-mono);
      font-size: var(--font-size-xs);
      letter-spacing: 0.07em;
      text-transform: uppercase;
      padding: 8px 16px;
      background: transparent;
      border-radius: var(--radius-btn);
      cursor: pointer;
      border: 1px solid var(--border);
      color: var(--text-muted);
    }
    .ego-btn--rent { border-color: var(--accent-orange); color: var(--accent-orange); }
    .ego-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  `;
  document.head.appendChild(style);
}
